import React, { ReactNode } from "react";
import styled from "@emotion/styled";
import { Box, Theme, useMediaQuery } from "@mui/material";
import { WithLoadingSpinner } from "@rentablo/components";
import { LoadingPlaceHolder } from "@rentablo/components";
import { Card } from "@rentablo/components";

type BottomSpacing = "small" | "medium";

const FigureContainer = styled.div<{ isFaded?: boolean }>`
  ${({ isFaded }) => (isFaded ? "opacity: 50%;" : "")};
`;

const ChartContainer = styled.div<{ isFaded?: boolean }>`
  position: relative;
  width: 100%;
  height: auto;
  ${({ isFaded }) => (isFaded ? "opacity: 50%;" : "")}
`;

const TopContainer = styled.div`
  width: 100%;
  padding: ${({ theme }) => theme.spacing(0.5, 0.5, 0)};
  gap: ${({ theme }) => theme.spacing(2)};
`;

const LegendContainer = styled.div<{
  isFaded?: boolean;
}>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  ${({ isFaded }) => (isFaded ? "opacity: 50%;" : "")};

  ${({ theme }) => theme.breakpoints.down("md")} {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }
`;

const BottomContainer = styled.div`
  width: 100%;
`;

const ChartCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== "bottomSpacing",
})<{ bottomSpacing?: BottomSpacing }>`
  padding: ${({ theme }) => theme.spacing(3, 4, 5, 4)};
`;

const ChartContent = styled.div`
  width: 100%;
`;

const ToolbarContainer = styled.div`
  overflow-x: hidden;
  width: 100%;
  margin: ${({ theme }) => theme.spacing(2, 0)};
  ${({ theme }) => theme.breakpoints.down("md")} {
    margin-bottom: ${({ theme }) => theme.spacing(6)};
  }
`;

const TooltipContainer = styled.div`
  overflow-x: hidden;
  width: 100%;
`;

const ChartTypeTabsContainer = styled.div`
  width: 100%;
  margin-bottom: ${({ theme }) => theme.spacing(3)};
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
`;

const RangeSelectorContainer = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(2)};
  ${({ theme }) => theme.breakpoints.down("md")} {
    margin: ${({ theme }) => theme.spacing(5, 0, 4)};
  }
  ${({ theme }) => theme.breakpoints.down("sm")} {
    width: 100%;
  }
`;

interface Props {
  loaderHeight?: string;
  intervalSelector?: ReactNode;
  figure: ReactNode;
  figureAction?: ReactNode;
  children: ReactNode;
  legend?: ReactNode;
  toolbar?: ReactNode;
  tooltip?: ReactNode;
  isContentFaded?: boolean;
  className?: string;
  isLoading?: boolean;
  isFetching?: boolean;
  showTimeBrush?: boolean;
  onOpenTimeBrushClick?: () => void;
  bottomComponent?: ReactNode;
  onChartMouseEnter?: React.MouseEventHandler<HTMLDivElement>;
  onChartMouseLeave?: React.MouseEventHandler<HTMLDivElement>;
  chartTypeTabs?: ReactNode;
  useCard?: boolean;
}

export const ChartLayout = ({
  children,
  loaderHeight,
  intervalSelector,
  figure,
  figureAction,
  legend,
  isContentFaded = false,
  className,
  isLoading,
  isFetching = false,
  bottomComponent,
  onChartMouseEnter,
  onChartMouseLeave,
  toolbar,
  tooltip,
  chartTypeTabs,
  useCard = true,
}: Props) => {
  const isLessMd = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md")
  );

  if (isLoading) {
    return <LoadingPlaceHolder className={className} height={loaderHeight} />;
  }

  const WrapperComponent = useCard ? ChartCard : Wrapper;
  const wrapperProps = useCard ? { size: "maxi" as const } : {};

  return (
    <WithLoadingSpinner isLoading={isFetching}>
      <WrapperComponent {...(wrapperProps as any)} className={className}>
        <TopContainer>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
            flexWrap="wrap"
            position="relative"
            width="100%"
            gap={1}
            mb={figureAction ? 4 : 0}
          >
            <FigureContainer isFaded={isContentFaded}>{figure}</FigureContainer>
            {figureAction && <Box alignSelf="flex-end">{figureAction}</Box>}
            {intervalSelector && !isLessMd && intervalSelector}
          </Box>
        </TopContainer>
        {isLessMd && intervalSelector != null && (
          <RangeSelectorContainer>{intervalSelector}</RangeSelectorContainer>
        )}
        {toolbar && <ToolbarContainer>{toolbar}</ToolbarContainer>}
        <ChartContent>
          {chartTypeTabs && (
            <ChartTypeTabsContainer>{chartTypeTabs}</ChartTypeTabsContainer>
          )}
          {tooltip && <TooltipContainer>{tooltip}</TooltipContainer>}

          <ChartContainer
            isFaded={isContentFaded}
            onMouseEnter={onChartMouseEnter}
            onMouseLeave={onChartMouseLeave}
          >
            {children}
          </ChartContainer>

          {legend && (
            <LegendContainer isFaded={isContentFaded}>{legend}</LegendContainer>
          )}

          {bottomComponent && (
            <BottomContainer>{bottomComponent}</BottomContainer>
          )}
        </ChartContent>
      </WrapperComponent>
    </WithLoadingSpinner>
  );
};
